export function createDefaultTribes() {
  return [
    { name: 'Kava', order: 0, startGrade: 0, endGrade: 1 },
    { name: 'Sulu', order: 1, startGrade: 2, endGrade: 3 },
    { name: 'Taka', order: 2, startGrade: 4, endGrade: 5 },
    { name: 'Masu', order: 3, startGrade: 6, endGrade: 8 },
  ];
}

export function deriveCoveredGradeRange(tribes) {
  const normalized = normalizeTribes(tribes);
  if (normalized.length === 0) {
    return { coveredGradeMin: null, coveredGradeMax: null };
  }
  return {
    coveredGradeMin: Math.min(...normalized.map((tribe) => tribe.startGrade)),
    coveredGradeMax: Math.max(...normalized.map((tribe) => tribe.endGrade)),
  };
}

export function normalizeCoveredGradeRange(location = {}) {
  const derived = deriveCoveredGradeRange(location.tribes || []);
  const coveredGradeMin = Number.isFinite(Number(location?.coveredGradeMin))
    ? Number(location.coveredGradeMin)
    : derived.coveredGradeMin;
  const coveredGradeMax = Number.isFinite(Number(location?.coveredGradeMax))
    ? Number(location.coveredGradeMax)
    : derived.coveredGradeMax;
  return { coveredGradeMin, coveredGradeMax };
}

export function normalizeTribes(tribes) {
  return (Array.isArray(tribes) ? tribes : [])
    .map((tribe, index) => ({
      id: tribe?.id ?? null,
      name: String(tribe?.name || '').trim(),
      order: Number.isFinite(Number(tribe?.order)) ? Number(tribe.order) : index,
      startGrade: Number.isFinite(Number(tribe?.startGrade)) ? Number(tribe.startGrade) : 0,
      endGrade: Number.isFinite(Number(tribe?.endGrade)) ? Number(tribe.endGrade) : 0,
    }))
    .sort((a, b) => a.order - b.order);
}

export function validateTribes(tribes) {
  const normalized = normalizeTribes(tribes);
  const errors = [];
  const seenNames = new Set();
  const seenGrades = new Map();
  normalized.forEach((tribe, index) => {
    if (!tribe.name) errors.push(`Tribe ${index + 1} must have a name.`);
    if (tribe.startGrade > tribe.endGrade) {
      errors.push(`Tribe ${tribe.name || index + 1} has start grade above end grade.`);
    }
    const lowered = tribe.name.toLowerCase();
    if (seenNames.has(lowered)) {
      errors.push(`Tribe name "${tribe.name}" is duplicated.`);
    }
    seenNames.add(lowered);
    for (let grade = tribe.startGrade; grade <= tribe.endGrade; grade += 1) {
      if (seenGrades.has(grade)) {
        errors.push(`Grade ${grade === 0 ? 'K' : grade} is assigned more than once.`);
      }
      seenGrades.set(grade, tribe.name);
    }
  });
  return { valid: errors.length === 0, errors, tribes: normalized };
}

export function validateLocationGradeCoverage(location = {}) {
  const errors = [];
  const { coveredGradeMin, coveredGradeMax } = normalizeCoveredGradeRange(location);
  if (!Number.isInteger(coveredGradeMin) || !Number.isInteger(coveredGradeMax)) {
    errors.push('Covered grade range is required.');
    return { valid: false, errors, coveredGradeMin, coveredGradeMax };
  }
  if (coveredGradeMin > coveredGradeMax) {
    errors.push('Covered grade minimum cannot exceed covered grade maximum.');
    return { valid: false, errors, coveredGradeMin, coveredGradeMax };
  }
  const validation = validateTribes(location.tribes || []);
  if (!validation.valid) {
    return { valid: false, errors: validation.errors.slice(), coveredGradeMin, coveredGradeMax };
  }
  const assignedGrades = new Set();
  validation.tribes.forEach((tribe) => {
    for (let grade = tribe.startGrade; grade <= tribe.endGrade; grade += 1) {
      assignedGrades.add(grade);
    }
  });
  for (let grade = coveredGradeMin; grade <= coveredGradeMax; grade += 1) {
    if (!assignedGrades.has(grade)) {
      errors.push(`Grade ${grade === 0 ? 'K' : grade} is not covered by any tribe.`);
    }
  }
  return { valid: errors.length === 0, errors, coveredGradeMin, coveredGradeMax };
}

export function getTribeForGrade(grade, tribes) {
  const normalized = normalizeTribes(tribes);
  const numericGrade = Number.isFinite(Number(grade)) ? Number(grade) : 0;
  const match = normalized.find((tribe) => numericGrade >= tribe.startGrade && numericGrade <= tribe.endGrade);
  return match ? match.name : 'Unassigned';
}

export function mapCampersToLocation(campers, location) {
  if (!location || !Array.isArray(location.tribes) || location.tribes.length === 0) return campers;
  return campers.map((camper) => ({
    ...camper,
    tribe: getTribeForGrade(camper.grade, location.tribes),
  }));
}

export function findCampersOutsideLocationCoverage(campers, location) {
  if (!location) return [];
  const { coveredGradeMin, coveredGradeMax } = normalizeCoveredGradeRange(location);
  if (!Number.isInteger(coveredGradeMin) || !Number.isInteger(coveredGradeMax)) return [];
  return (Array.isArray(campers) ? campers : []).filter((camper) => {
    const grade = Number.isFinite(Number(camper?.grade)) ? Number(camper.grade) : 0;
    return grade < coveredGradeMin || grade > coveredGradeMax || getTribeForGrade(grade, location.tribes || []) === 'Unassigned';
  });
}

export function getTribeNames(location) {
  if (!location || !Array.isArray(location.tribes)) return [];
  return normalizeTribes(location.tribes).map((tribe) => tribe.name);
}

function extractTribes(locationOrTribes) {
  if (Array.isArray(locationOrTribes)) return locationOrTribes;
  if (locationOrTribes && Array.isArray(locationOrTribes.tribes)) return locationOrTribes.tribes;
  return createDefaultTribes();
}

export function buildTribeAdjacencyMap(locationOrTribes) {
  const tribes = normalizeTribes(extractTribes(locationOrTribes));
  const adjacency = {};
  tribes.forEach((tribe, index) => {
    const neighbors = new Set([tribe.name]);
    if (index > 0) neighbors.add(tribes[index - 1].name);
    if (index < tribes.length - 1) neighbors.add(tribes[index + 1].name);
    adjacency[tribe.name] = Array.from(neighbors);
  });
  return adjacency;
}

export function tribesAreAdjacent(tribeA, tribeB, locationOrTribes) {
  if (!tribeA || !tribeB) return false;
  if (tribeA === tribeB) return true;
  const adjacency = buildTribeAdjacencyMap(locationOrTribes);
  return Array.isArray(adjacency[tribeA]) && adjacency[tribeA].includes(tribeB);
}

export function canGroupTribes(existingTribes, candidateTribe, locationOrTribes) {
  const tribes = normalizeTribes(extractTribes(locationOrTribes));
  const orderByName = new Map(tribes.map((tribe) => [tribe.name, tribe.order]));
  const uniqueTribes = new Set(Array.isArray(existingTribes) ? existingTribes.filter(Boolean) : []);
  if (candidateTribe) uniqueTribes.add(candidateTribe);
  if (uniqueTribes.size <= 1) return true;
  if (uniqueTribes.size > 2) return false;
  const orders = Array.from(uniqueTribes).map((tribe) => orderByName.get(tribe));
  if (orders.some((order) => !Number.isInteger(order))) return false;
  return Math.max(...orders) - Math.min(...orders) <= 1;
}

export function listContiguousTribeWindows(locationOrTribes, allowedTribes = []) {
  const tribes = normalizeTribes(extractTribes(locationOrTribes));
  const allowed = new Set((Array.isArray(allowedTribes) && allowedTribes.length > 0)
    ? allowedTribes
    : tribes.map((tribe) => tribe.name));
  const windows = [];
  tribes.forEach((tribe, index) => {
    if (!allowed.has(tribe.name)) return;
    windows.push([tribe.name]);
    const next = tribes[index + 1];
    if (next && allowed.has(next.name)) {
      windows.push([tribe.name, next.name]);
    }
  });
  return windows;
}
